// DomainCard — white-labeled domain search + purchase surface that
// slides in next to the WebsitePreviewCard. Hits the backend at
// POST /api/domains/search and POST /api/domains/purchase. Falls
// back to a graceful placeholder result list when the endpoints
// aren't reachable so the demo flow still feels alive.
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { FONT, TEXT, TEXT2, TEXT3, BLUE, BLUE_DEEP, BLUE_LIGHT, BORDER, WHITE, SUCCESS } from '../../shared';
import { searchDomains, purchaseDomain } from '../../api';

const TLDS = ['.com', '.net', '.org', '.io', '.co'];

function slugify(name) {
  return (name || '').toLowerCase().replace(/[^a-z0-9]+/g, '').slice(0, 24);
}

function defaultResultsFor(query) {
  // Local fallback so the card has something to render before the
  // backend wires up /api/domains/search. Prices are placeholders.
  const base = slugify(query) || 'yoursite';
  return TLDS.map((tld, i) => ({
    domain: `${base}${tld}`,
    tld,
    available: i !== 0 || base.length > 6,
    price: tld === '.com' ? 12.99 : tld === '.io' ? 39.99 : tld === '.co' ? 24.99 : 14.99,
    currency: 'USD',
  }));
}

export default function DomainCard({ businessName, onClose, onAction }) {
  const initial = slugify(businessName) || '';
  const [query, setQuery] = useState(initial);
  const [results, setResults] = useState(initial ? defaultResultsFor(initial) : []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [purchasing, setPurchasing] = useState(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [connectMode, setConnectMode] = useState(false);
  const [connectValue, setConnectValue] = useState('');
  const cardRef = useRef(null);

  const search = useCallback(async (q) => {
    const term = (q || query || '').trim();
    if (!term) return;
    setLoading(true);
    setError('');
    try {
      const data = await searchDomains(term);
      const list = Array.isArray(data?.results) ? data.results : (Array.isArray(data?.available) ? data.available : null);
      if (list && list.length > 0) {
        setResults(list);
      } else {
        setResults(defaultResultsFor(term));
      }
    } catch (e) {
      setResults(defaultResultsFor(term));
      setError(''); // silent fallback so the demo flow stays smooth
    } finally {
      setLoading(false);
    }
  }, [query]);

  // Auto-search on mount when seeded with a business name.
  const ranInitialRef = useRef(false);
  useEffect(() => {
    if (ranInitialRef.current) return;
    ranInitialRef.current = true;
    if (initial) search(initial);
  }, [initial, search]);

  const onSubmit = useCallback((e) => {
    e.preventDefault();
    search(query);
  }, [query, search]);

  const onMouseMove = useCallback((e) => {
    const r = cardRef.current?.getBoundingClientRect();
    if (!r) return;
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top)  / r.height;
    setTilt({ x: (py - 0.5) * -6, y: (px - 0.5) * 8 });
  }, []);
  const onMouseLeave = useCallback(() => setTilt({ x: 0, y: 0 }), []);

  const buy = useCallback(async (item) => {
    if (!item || !item.available) return;
    setPurchasing(item.domain);
    setError('');
    try {
      const data = await purchaseDomain(item.domain);
      if (data && (data.ok || data.orderId || data.confirmUrl)) {
        if (onAction) onAction({ type: 'domain_purchased', data: { domain: item.domain, ...data } });
        setError('');
        // Mark as purchased in the local results list.
        setResults((prev) => prev.map((r) => r.domain === item.domain ? { ...r, purchased: true } : r));
      } else {
        setError(data?.error || 'Could not start checkout. Please try again.');
      }
    } catch (e) {
      setError(e?.message || 'Could not start checkout. Please try again.');
    } finally {
      setPurchasing(null);
    }
  }, [onAction]);

  const connect = useCallback(() => {
    const v = (connectValue || '').trim().toLowerCase();
    if (!v) return;
    if (onAction) onAction({ type: 'domain_connect_existing', data: { domain: v } });
    setConnectMode(false);
    setConnectValue('');
  }, [connectValue, onAction]);

  return (
    <div
      ref={cardRef}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      style={{
        width: '100%',
        borderRadius: 18,
        overflow: 'hidden',
        border: `1px solid ${BORDER}`,
        background: 'linear-gradient(135deg, rgba(255,255,255,0.94) 0%, rgba(255,255,255,0.80) 100%)',
        backdropFilter: 'blur(28px) saturate(180%)',
        WebkitBackdropFilter: 'blur(28px) saturate(180%)',
        boxShadow: '0 1px 0 rgba(255,255,255,0.85) inset, 0 24px 50px -20px rgba(11,107,211,0.32), 0 10px 30px -14px rgba(14,165,233,0.20)',
        fontFamily: FONT,
        transform: `perspective(1000px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
        transition: 'transform 0.35s cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 0.25s ease',
      }}
    >
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 14px', borderBottom: `1px solid ${BORDER}`,
        background: 'rgba(255,255,255,0.55)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <div style={{
            width: 28, height: 28, borderRadius: 8,
            background: 'linear-gradient(135deg, #6366F1, #4338CA)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 12px -4px rgba(99,102,241,0.5)',
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12h18"/>
              <path d="M12 3a15 15 0 010 18"/>
              <path d="M12 3a15 15 0 000 18"/>
            </svg>
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: TEXT, lineHeight: 1.15 }}>Choose a domain</div>
            <div style={{ fontSize: 11, color: TEXT3, lineHeight: 1.15 }}>Connect or buy a new one</div>
          </div>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close domains"
            style={{
              width: 24, height: 24, borderRadius: '50%',
              border: `1px solid ${BORDER}`, background: WHITE,
              cursor: 'pointer', display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center', padding: 0,
            }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={TEXT2} strokeWidth="2.4" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        )}
      </div>

      {/* Search */}
      <form onSubmit={onSubmit} style={{ padding: '12px 14px', borderBottom: `1px solid ${BORDER}` }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: WHITE, border: `1px solid ${BORDER}`,
          borderRadius: 10, padding: '6px 10px',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={TEXT3} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search for a domain..."
            style={{
              flex: 1, border: 'none', outline: 'none',
              fontFamily: FONT, fontSize: 13, color: TEXT,
              background: 'transparent', padding: '4px 0',
            }}
          />
          <button
            type="submit"
            disabled={loading}
            style={{
              fontSize: 11, fontWeight: 700, color: '#fff',
              padding: '5px 10px', borderRadius: 999, border: 'none',
              background: loading ? '#94A3B8' : 'linear-gradient(135deg, #3B82F6, #2563EB)',
              cursor: loading ? 'default' : 'pointer', fontFamily: FONT,
            }}
          >
            {loading ? 'Searching…' : 'Search'}
          </button>
        </div>
      </form>

      {/* Results */}
      <div style={{ maxHeight: 380, overflowY: 'auto' }}>
        {results.length === 0 && !loading && (
          <div style={{ padding: 20, textAlign: 'center', fontSize: 12, color: TEXT3 }}>
            Type a name to see available domains.
          </div>
        )}
        {results.map((r) => (
          <div
            key={r.domain}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 10, padding: '10px 14px', borderBottom: `1px solid ${BORDER}`,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: TEXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {r.domain}
              </div>
              <div style={{ fontSize: 11, color: r.available ? SUCCESS : TEXT3, fontWeight: 600, marginTop: 2 }}>
                {r.purchased
                  ? 'Purchased — setting up'
                  : r.available
                    ? `Available · $${Number(r.price).toFixed(2)}/yr`
                    : 'Taken'}
              </div>
            </div>
            <button
              type="button"
              disabled={!r.available || purchasing === r.domain || r.purchased}
              onClick={() => buy(r)}
              style={{
                flexShrink: 0,
                fontSize: 11, fontWeight: 700,
                padding: '6px 12px', borderRadius: 999,
                border: r.available && !r.purchased ? 'none' : `1px solid ${BORDER}`,
                background: r.purchased
                  ? BLUE_LIGHT
                  : r.available
                    ? 'linear-gradient(135deg, #10B981, #059669)'
                    : WHITE,
                color: r.purchased ? BLUE_DEEP : r.available ? '#fff' : TEXT3,
                cursor: r.available && !r.purchased && purchasing !== r.domain ? 'pointer' : 'default',
                fontFamily: FONT,
                opacity: purchasing === r.domain ? 0.7 : 1,
                minWidth: 64,
              }}
            >
              {r.purchased
                ? 'Owned'
                : purchasing === r.domain
                  ? '…'
                  : r.available
                    ? 'Buy'
                    : '—'}
            </button>
          </div>
        ))}
      </div>

      {error && (
        <div style={{ padding: '8px 14px', fontSize: 11, color: '#B91C1C', background: '#FEF2F2', borderTop: `1px solid #FECACA` }}>
          {error}
        </div>
      )}

      {/* Connect existing */}
      <div style={{ padding: '12px 14px', background: 'rgba(255,255,255,0.55)' }}>
        {!connectMode ? (
          <button
            type="button"
            onClick={() => setConnectMode(true)}
            style={{
              width: '100%', fontSize: 12, fontWeight: 700, color: BLUE_DEEP,
              padding: '8px 12px', borderRadius: 10,
              border: `1px solid ${BORDER}`, background: WHITE,
              cursor: 'pointer', fontFamily: FONT,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
              <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
            </svg>
            Connect existing domain
          </button>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input
              type="text"
              value={connectValue}
              onChange={(e) => setConnectValue(e.target.value)}
              placeholder="yourdomain.com"
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '8px 10px', borderRadius: 10,
                border: `1px solid ${BORDER}`, background: WHITE,
                fontFamily: FONT, fontSize: 13, color: TEXT,
              }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => { setConnectMode(false); setConnectValue(''); }}
                style={{
                  flex: 1, fontSize: 12, fontWeight: 700, color: TEXT2,
                  padding: '8px 12px', borderRadius: 10,
                  border: `1px solid ${BORDER}`, background: WHITE,
                  cursor: 'pointer', fontFamily: FONT,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={connect}
                style={{
                  flex: 1, fontSize: 12, fontWeight: 700, color: '#fff',
                  padding: '8px 12px', borderRadius: 10, border: 'none',
                  background: 'linear-gradient(135deg, #3B82F6, #2563EB)',
                  cursor: 'pointer', fontFamily: FONT,
                }}
              >
                Connect
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
